import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { AgxImageUploader, ImageFile } from '@urbanx/agx.ui-components2';
import { UploadedDocument, UploadedDocumentDocumentType } from '@urbanx/agx.campaigns.client';
import { uploadFile, getFileLink, deleteFile } from 'services';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { INITIAL_RETRY_DELAY_MS, MAX_RETRY_ATTEMPTS, retryWithDelay } from './PhotosAndMediaHelper';

interface CampaignImageUploaderProps {
  campaignId: string;
  documentType: UploadedDocumentDocumentType;
  documents: UploadedDocument[] | undefined;
  maxImages: number;
  maxFileSizeMB?: number;
  getAuthToken: (() => Promise<string | undefined>) | undefined;
  onDocumentsChange: (updated: UploadedDocument[]) => void;
  onToast: (state: { color: 'success' | 'error'; message: string }) => void;
  onBusyStateChange?: (isBusy: boolean) => void;
  dataTestId?: string;
  id: string;
}

const ACCEPTED_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/gif'];

export const CampaignImageUploader = ({
  campaignId,
  documentType,
  documents,
  maxImages,
  maxFileSizeMB = 15,
  getAuthToken,
  onDocumentsChange,
  onToast,
  onBusyStateChange,
  dataTestId,
  id,
}: CampaignImageUploaderProps) => {
  const [localDocs, setLocalDocs] = useState<UploadedDocument[]>([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [resolvedLinks, setResolvedLinks] = useState<Map<string, string>>(new Map());
  const [localPreviews, setLocalPreviews] = useState(new Map<string, string>());

  // Tracking state refs
  const activeUploadsRef = useRef(new Set<string>());
  const pendingResolutionsRef = useRef(new Set<string>());
  const uploaderIdToDocIdRef = useRef(new Map<string, { docId: string; order: number }>());
  const isInternalUpdateRef = useRef(false);
  const pendingRemovalRef = useRef(false);

  // To keep the localDocs accessible in callbacks without stale closures
  const localDocsRef = useRef(localDocs);
  useEffect(() => {
    localDocsRef.current = localDocs;
  }, [localDocs]);

  const notifyBusyState = useCallback(() => {
    const isBusy = activeUploadsRef.current.size > 0 || pendingResolutionsRef.current.size > 0;
    onBusyStateChange?.(isBusy);
  }, [onBusyStateChange]);

  const displayImages = useMemo(
    (): ImageFile[] =>
      localDocs.map((doc, idx) => {
        const resolvedUrl =
          doc.publicPath ||
          (doc.containerFilePath ? resolvedLinks.get(doc.containerFilePath) : '') ||
          '';
        const localPreview = localPreviews.get(doc.documentId ?? '');
        const hasUrl = !!resolvedUrl;

        return {
          id: doc.documentId ?? '',
          fileName: doc.fileName || '',
          publicPath: resolvedUrl,
          preview: resolvedUrl || localPreview || placeHolderImage,
          status: hasUrl ? 'uploaded' : 'uploading',
          order: idx,
          progress: hasUrl ? 100 : localPreview ? 90 : 80,
        };
      }),
    [localDocs, localPreviews, resolvedLinks]
  );

  // Sync existing images from props
  useEffect(() => {
    if (documents === undefined) return;

    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (!isInitialized) {
      const links = new Map<string, string>();
      documents.forEach((doc) => {
        if (doc.publicPath && doc.containerFilePath) {
          links.set(doc.containerFilePath, doc.publicPath);
        }
      });
      setResolvedLinks(links);
      setLocalDocs([...documents]);
      setIsInitialized(true);
      if (documents.length) setUploaderKey((k) => k + 1);
      return;
    }

    const currentDocIds = localDocs
      .map((d) => d.documentId)
      .sort()
      .join(',');
    const newDocIds = documents
      .map((d) => d.documentId)
      .sort()
      .join(',');

    if (newDocIds === currentDocIds) {
      let hasChanges = false;
      const updatedDocs = localDocs.map((localDoc) => {
        const ext = documents.find((d) => d.documentId === localDoc.documentId);
        if (ext?.publicPath && !localDoc.publicPath) {
          hasChanges = true;
          return { ...localDoc, publicPath: ext.publicPath };
        }
        return localDoc;
      });

      if (hasChanges) {
        setLocalDocs(updatedDocs);
        setResolvedLinks((prev) => {
          const next = new Map(prev);
          documents.forEach((doc) => {
            if (doc.publicPath && doc.containerFilePath) {
              next.set(doc.containerFilePath, doc.publicPath);
            }
          });
          return next;
        });
      }
      return;
    }

    const links = new Map(resolvedLinks);
    documents.forEach((doc) => {
      if (doc.publicPath && doc.containerFilePath) {
        links.set(doc.containerFilePath, doc.publicPath);
      }
    });
    uploaderIdToDocIdRef.current.clear();
    setResolvedLinks(links);
    setLocalDocs([...documents]);
    setUploaderKey((k) => k + 1);
  }, [documents, isInitialized, localDocs, resolvedLinks]);

  // Resolve image links for uploaded images that lack publicPath
  useEffect(() => {
    const unresolvedDocs = localDocs.filter(
      (doc) =>
        !doc.publicPath &&
        doc.containerFilePath &&
        !resolvedLinks.has(doc.containerFilePath) &&
        !pendingResolutionsRef.current.has(doc.containerFilePath)
    );

    if (!unresolvedDocs.length) return;

    const resolveAll = async () => {
      const authToken = await getAuthToken?.();
      if (!authToken) return;

      unresolvedDocs.forEach((doc) => {
        pendingResolutionsRef.current.add(doc.containerFilePath!);
      });
      notifyBusyState();

      const results = await Promise.all(
        unresolvedDocs.map(async (doc) => {
          const path = doc.containerFilePath!;
          try {
            const link = await retryWithDelay(
              () => getFileLink(authToken, path) as Promise<any>,
              MAX_RETRY_ATTEMPTS,
              INITIAL_RETRY_DELAY_MS
            );
            return { path, link, docId: doc.documentId };
          } catch {
            return { path, link: '', docId: doc.documentId };
          } finally {
            pendingResolutionsRef.current.delete(path);
          }
        })
      );

      notifyBusyState();

      const successfulResults = results.filter((r) => r.link);

      if (successfulResults.length > 0) {
        setResolvedLinks((prev) => {
          const next = new Map(prev);
          successfulResults.forEach((r) => next.set(r.path, r.link));
          return next;
        });

        setLocalDocs((prev) => {
          // Map docId to the image upload order
          const orderMap = new Map<string, number>();
          for (const [, { docId, order }] of uploaderIdToDocIdRef.current.entries()) {
            orderMap.set(docId, order);
          }
          // Update docs with publicPath and sort by upload order
          const updated = prev.map((doc) => {
            const result = successfulResults.find((r) => r.docId === doc.documentId);
            return result ? { ...doc, publicPath: result.link } : doc;
          });
          updated.sort(
            (a, b) =>
              (orderMap.get(a.documentId ?? '') ?? 0) - (orderMap.get(b.documentId ?? '') ?? 0)
          );
          return updated;
        });
      }
    };

    void resolveAll();
  }, [localDocs, resolvedLinks, getAuthToken, notifyBusyState]);

  const findDocByImageId = useCallback((imageId: string): UploadedDocument | undefined => {
    const docs = localDocsRef.current;

    let doc = docs.find((d) => d.documentId === imageId);
    if (doc) return doc;

    const mappedDocId = uploaderIdToDocIdRef.current.get(imageId);
    if (mappedDocId) {
      doc = docs.find((d) => d.documentId === mappedDocId.docId);
      if (doc) return doc;
    }

    return undefined;
  }, []);

  const handleUpload = useCallback(
    async (imageFile: ImageFile, reportProgress: (p: number) => void) => {
      const authToken = await getAuthToken?.();
      if (!authToken) {
        onToast({ color: 'error', message: 'Auth failed for upload' });
        reportProgress(100);
        throw new Error('Auth failed for upload');
      }

      const file = imageFile.file;
      if (!file) {
        onToast({ color: 'error', message: 'No file data' });
        reportProgress(100);
        throw new Error('No file data');
      }

      if (!ACCEPTED_IMAGE_FORMATS.includes(file.type)) {
        onToast({ color: 'error', message: 'Files must be in JPG, GIF, or PNG format only' });
        reportProgress(100);
        throw new Error('Invalid file type');
      }

      activeUploadsRef.current.add(imageFile.id);
      notifyBusyState();

      const localPreview = URL.createObjectURL(file);
      reportProgress(20);

      try {
        const formData = new FormData();
        formData.append('File', file);
        reportProgress(40);

        const uploadPath = `${campaignId}/CampaignPhotosAndMediaDetails/${file.name}`;
        const uploaded = await retryWithDelay(
          () =>
            uploadFile(
              authToken,
              formData as any,
              campaignId,
              documentType as any,
              null,
              null,
              uploadPath
            ),
          MAX_RETRY_ATTEMPTS,
          INITIAL_RETRY_DELAY_MS
        );
        reportProgress(60);

        uploaderIdToDocIdRef.current.set(imageFile.id, {
          docId: uploaded.documentId,
          order: localDocsRef.current.length,
        });

        let publicPath = uploaded.publicPath || '';
        if (!publicPath && uploaded.containerFilePath) {
          publicPath = await retryWithDelay(
            () => getFileLink(authToken, uploaded.containerFilePath) as Promise<any>,
            MAX_RETRY_ATTEMPTS,
            INITIAL_RETRY_DELAY_MS
          );
        }
        reportProgress(90);

        if (publicPath) {
          URL.revokeObjectURL(localPreview);
          if (uploaded.containerFilePath) {
            setResolvedLinks((prev) => new Map(prev).set(uploaded.containerFilePath!, publicPath));
          }
        } else {
          setLocalPreviews((previews) => new Map(previews).set(uploaded.documentId, localPreview));
        }

        const newDoc: UploadedDocument = { ...uploaded, fileName: file.name, publicPath };
        const updated = [...localDocsRef.current, newDoc];
        localDocsRef.current = updated;
        isInternalUpdateRef.current = true;
        setLocalDocs(updated);
        onDocumentsChange(updated);

        reportProgress(100);
      } catch {
        URL.revokeObjectURL(localPreview);
        reportProgress(100);
        throw new Error('Upload failed');
      } finally {
        activeUploadsRef.current.delete(imageFile.id);
        notifyBusyState();
      }
    },
    [campaignId, documentType, getAuthToken, notifyBusyState, onDocumentsChange, onToast]
  );

  const handleRemove = useCallback(
    async (imageId: string) => {
      const authToken = await getAuthToken?.();
      if (!authToken) {
        onToast({ color: 'error', message: 'Auth failed for removal' });
        return;
      }

      pendingRemovalRef.current = true;

      const doc = findDocByImageId(imageId);
      if (!doc) {
        onToast({ color: 'error', message: 'Image not found' });
        pendingRemovalRef.current = false;
        return;
      }

      if (doc.containerFilePath) {
        try {
          await retryWithDelay(
            () => deleteFile(authToken, doc.containerFilePath ?? ''),
            MAX_RETRY_ATTEMPTS,
            INITIAL_RETRY_DELAY_MS
          );
        } catch (err) {
          console.error('Failed to delete file:', err);
        }
      }

      const preview = localPreviews.get(doc.documentId ?? '');
      if (preview) {
        URL.revokeObjectURL(preview);
        setLocalPreviews((previews) => {
          const next = new Map(previews);
          next.delete(doc.documentId ?? '');
          return next;
        });
      }

      for (const [uploaderId, mappedDocId] of uploaderIdToDocIdRef.current.entries()) {
        if (mappedDocId.docId === doc.documentId) {
          uploaderIdToDocIdRef.current.delete(uploaderId);
          break;
        }
      }

      if (doc.containerFilePath) {
        setResolvedLinks((prev) => {
          const next = new Map(prev);
          next.delete(doc.containerFilePath!);
          return next;
        });
      }

      const updated = localDocsRef.current.filter((d) => d.documentId !== doc.documentId);
      localDocsRef.current = updated;
      isInternalUpdateRef.current = true;
      setLocalDocs(updated);
      onDocumentsChange(updated);

      onToast({ color: 'success', message: 'Image removed' });

      setTimeout(() => {
        pendingRemovalRef.current = false;
      }, 0);
    },
    [findDocByImageId, getAuthToken, localPreviews, onDocumentsChange, onToast]
  );

  const handleFilesChange = useCallback(
    (images: ImageFile[]) => {
      setTimeout(() => {
        const docs = localDocsRef.current;
        // Always update the mapping for all images to keep the upload order in sync
        images.forEach((img, idx) => {
          let docId = img.id;
          const mapped = uploaderIdToDocIdRef.current.get(img.id);
          if (mapped) {
            docId = mapped.docId;
          } else if (idx < docs.length && img.id !== docs[idx].documentId) {
            docId = docs[idx].documentId ?? img.id;
          }
          uploaderIdToDocIdRef.current.set(img.id, { docId, order: idx });
        });

        const reordered = images
          .map((img) => findDocByImageId(img.id))
          .filter((d): d is UploadedDocument => !!d);

        if (reordered.length !== images.length) return;

        isInternalUpdateRef.current = true;
        localDocsRef.current = reordered;
        setLocalDocs(reordered);
        onDocumentsChange(reordered);
      }, 0);
    },
    [findDocByImageId, onDocumentsChange]
  );

  if (documents === undefined || !isInitialized) return null;

  return (
    <AgxImageUploader
      key={`${id}-${uploaderKey}`}
      id={id}
      maxImages={maxImages}
      maxFileSizeMB={maxFileSizeMB}
      accept={ACCEPTED_IMAGE_FORMATS.join(', ')}
      initialImages={displayImages}
      onFilesChange={handleFilesChange}
      onUpload={handleUpload}
      onImageRemove={(imageId) => void handleRemove(imageId)}
      dataTestId={dataTestId}
    />
  );
};
